import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './VarianceReport.css';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const contains = (value, query) => String(value ?? '').toLowerCase().includes(String(query ?? '').trim().toLowerCase());

const VarianceReport = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, user, isAdmin } = useAuth();
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    type: '',
    transactionId: '',
    partNumber: '',
    partDescription: '',
    expectedQuantity: '',
    actualQuantity: '',
    variance: '',
    recordedAt: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let url = '/api/reports/variance';
      if (isAdmin && selectedLocationId !== '') {
        url += `?consignment_location_id=${encodeURIComponent(selectedLocationId)}`;
      }
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to load variance reports');
      const data = await response.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load variance reports');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAdmin, selectedLocationId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredRecords = useMemo(
    () =>
      records.filter((row) => {
        if (columnFilters.type && (row.type || '').toLowerCase() !== columnFilters.type.toLowerCase()) return false;
        if (!contains(row.transactionId, columnFilters.transactionId)) return false;
        if (!contains(row.partNumber, columnFilters.partNumber)) return false;
        if (!contains(row.partDescription, columnFilters.partDescription)) return false;
        if (!contains(row.expectedQuantity, columnFilters.expectedQuantity)) return false;
        if (!contains(row.actualQuantity, columnFilters.actualQuantity)) return false;
        if (columnFilters.variance) {
          const v = Number(row.variance);
          if (columnFilters.variance === 'positive' && (!Number.isFinite(v) || v <= 0)) return false;
          if (columnFilters.variance === 'negative' && (!Number.isFinite(v) || v >= 0)) return false;
        }
        if (!contains(formatDate(row.recordedAt), columnFilters.recordedAt)) return false;
        return true;
      }),
    [records, columnFilters]
  );

  const handleFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const rows = filteredRecords.map((row) => ({
      Type: row.type || '',
      'Transaction ID': row.transactionId || '',
      'Part number': row.partNumber || '',
      Description: row.partDescription || '',
      Expected: row.expectedQuantity != null ? String(row.expectedQuantity) : '',
      Actual: row.actualQuantity != null ? String(row.actualQuantity) : '',
      Variance: row.variance != null ? String(row.variance) : '',
      Recorded: row.recordedAt ? new Date(row.recordedAt).toLocaleString() : '',
    }));
    exportRowsToExcel(`variance-report-${stamp}`, 'Variance', rows);
  };

  return (
    <Layout>
      <div className="variance-container">
        <div className="variance-header">
          <div className="variance-header-left">
            <button type="button" className="variance-back" onClick={() => navigate('/reports')}>
              ← Back to Reports
            </button>
            <h1>Variance Report</h1>
          </div>
          <div className="variance-header-actions">
            {isAdmin && (
              <label className="variance-filter">
                <span className="variance-filter-label">Location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="variance-filter-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location #${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isAdmin && user?.consignmentLocationName && (
              <span className="variance-badge">Location: {user.consignmentLocationName}</span>
            )}
            <button type="button" className="variance-refresh" onClick={loadRecords}>
              🔄 Refresh
            </button>
            <button type="button" className="variance-refresh" onClick={handleExport} disabled={filteredRecords.length === 0}>
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="variance-subtitle">
          Expected vs actual quantity from Receiving and Putaway. Positive variance = over; negative = short.
        </p>
        {error && <div className="variance-error">{error}</div>}
        {isLoading ? (
          <div className="variance-loading">Loading...</div>
        ) : records.length === 0 ? (
          <div className="variance-empty">
            <div className="variance-empty-icon">📉</div>
            <h2>No variance records</h2>
            <p>Variances appear when operators enter a quantity that does not match expected in Receiving or Putaway.</p>
          </div>
        ) : (
          <div className="variance-table-wrap">
            <table className="variance-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Transaction ID</th>
                  <th>Part Number</th>
                  <th>Description</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Variance</th>
                  <th>Recorded</th>
                </tr>
                <tr className="variance-filter-row">
                  <th>
                    <select
                      value={columnFilters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      className="variance-col-filter variance-col-filter-select"
                    >
                      <option value="">All</option>
                      <option value="receiving">Receiving</option>
                      <option value="putaway">Putaway</option>
                    </select>
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.transactionId}
                      onChange={(e) => handleFilterChange('transactionId', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.partNumber}
                      onChange={(e) => handleFilterChange('partNumber', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.partDescription}
                      onChange={(e) => handleFilterChange('partDescription', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.expectedQuantity}
                      onChange={(e) => handleFilterChange('expectedQuantity', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.actualQuantity}
                      onChange={(e) => handleFilterChange('actualQuantity', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                  <th>
                    <select
                      value={columnFilters.variance}
                      onChange={(e) => handleFilterChange('variance', e.target.value)}
                      className="variance-col-filter variance-col-filter-select"
                      aria-label="Filter by variance sign"
                    >
                      <option value="">All</option>
                      <option value="positive">Positive (+ve)</option>
                      <option value="negative">Negative (−ve)</option>
                    </select>
                  </th>
                  <th>
                    <input
                      type="text"
                      value={columnFilters.recordedAt}
                      onChange={(e) => handleFilterChange('recordedAt', e.target.value)}
                      className="variance-col-filter"
                      placeholder="Filter"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className={`variance-type variance-type-${(row.type || '').toLowerCase()}`}>
                        {(row.type || '').charAt(0).toUpperCase() + (row.type || '').slice(1) || '—'}
                      </span>
                    </td>
                    <td>{row.transactionId || '—'}</td>
                    <td>{row.partNumber || '—'}</td>
                    <td className="variance-desc">{(row.partDescription || '—').slice(0, 40)}{(row.partDescription || '').length > 40 ? '…' : ''}</td>
                    <td className="variance-num">{row.expectedQuantity}</td>
                    <td className="variance-num">{row.actualQuantity}</td>
                    <td>
                      <span className={row.variance >= 0 ? 'variance-over' : 'variance-short'}>
                        {row.variance >= 0 ? '+' : ''}{row.variance}
                      </span>
                    </td>
                    <td className="variance-date">{formatDate(row.recordedAt)}</td>
                  </tr>
                ))}
                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan={8} className="variance-no-filter-results">
                      No records match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VarianceReport;
